import { TOAST_DURATION, ToastVariant } from './toast.types';

/**
 * How long each variant stays without an explicit duration. Success and info
 * confirm something the user already expects, so they get out of the way;
 * warnings and errors carry something to read.
 */
const DURATION_BY_VARIANT: Record<ToastVariant, number> = {
  success: TOAST_DURATION.short,
  info: TOAST_DURATION.short,
  warning: TOAST_DURATION.medium,
  error: TOAST_DURATION.long,
};

/**
 * Anything with an action outranks its variant default — the timer is now a
 * deadline the user has to beat, not just a reading window.
 */
export const toastDefaultDuration = (variant: ToastVariant, hasAction: boolean): number =>
  hasAction ? TOAST_DURATION.extended : DURATION_BY_VARIANT[variant];

/**
 * Errors interrupt; everything else waits for a pause in speech. `alert` maps
 * to an assertive live region, `status` to a polite one.
 */
export const toastRole = (variant: ToastVariant): 'alert' | 'status' =>
  variant === 'error' ? 'alert' : 'status';

/** The live region a toast of this variant belongs in. */
export const toastPoliteness = (variant: ToastVariant): 'assertive' | 'polite' =>
  variant === 'error' ? 'assertive' : 'polite';

/**
 * Prefix read out before the message, so a screen reader user gets the outcome
 * before the detail. Sighted users get this from the icon and its colour.
 */
export const toastVariantLabel = (variant: ToastVariant): string =>
  ({ success: 'Success', error: 'Error', warning: 'Warning', info: 'Information' })[variant];
